'use client';

// Server-seeded mode context. Root layout resolves the mode once per
// request and hands the resolved view to this provider, so every
// <WorkspaceBanner> and <WriteAffordance> consumer renders the correct
// mode on first paint without firing its own /api/me fetch.
//
// This addresses two Codex review findings on #595:
//   P1 — <WriteAffordance> would leak children until client fetch resolved.
//        Server seeding means `resolved: true` from the first render.
//   P2 — Per-hook /api/me fetches multiplied with the documented pattern
//        of wrapping many write surfaces in <WriteAffordance>. The
//        provider centralizes the read so N consumers share 1 value.

import { createContext, useContext, type ReactNode } from 'react';

import type { ModeView } from '@/hooks/use-mode';

const ModeContext = createContext<ModeView | null>(null);

export function ModeProvider({
  value,
  children,
}: {
  value: ModeView;
  children: ReactNode;
}) {
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

// Returns the seeded ModeView when a provider is mounted, or null in
// provider-less environments (unit tests without layout wiring). When
// null, consumers fall back to useMode's local-fetch path.
export function useOptionalModeContext(): ModeView | null {
  return useContext(ModeContext);
}
